import type { CfBlock, CfDxf, CfRuleParsed } from '../model/conditionalFormatModel';
import { sqrefToRects } from '../model/conditionalFormatModel';
import type {
  XlsxAutoFilterColumn,
  XlsxAutoFilterCustom,
} from '../model/types';
import type { XlsxParsedSheetFeatures, XlsxParsedValidation } from './types';
import { firstElementBody, parseXmlAttributes, stripXmlTags, xmlUnescape } from './xml';

function parseStaticList(formula: string): string[] | null {
  let value = xmlUnescape(formula.trim());
  if (value.startsWith('=')) value = value.slice(1);
  if (!value.startsWith('"') || !value.endsWith('"') || value.length < 2) return null;
  return value
    .slice(1, -1)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

const OOXML_FILTER_OPERATORS: Record<string, XlsxAutoFilterCustom['op']> = {
  greaterThan: 'gt',
  greaterThanOrEqual: 'ge',
  lessThan: 'lt',
  lessThanOrEqual: 'le',
  notEqual: 'ne',
  equal: 'eq',
};

function parseCustomFilter(
  operator: string | undefined,
  rawValue: string,
): XlsxAutoFilterCustom {
  const op = OOXML_FILTER_OPERATORS[operator ?? 'equal'] ?? 'eq';
  if (op === 'eq' || op === 'ne') {
    const startsWithStar = rawValue.startsWith('*');
    const endsWithStar = rawValue.length > (startsWithStar ? 1 : 0) && rawValue.endsWith('*');
    const inner = rawValue.slice(
      startsWithStar ? 1 : 0,
      endsWithStar ? rawValue.length - 1 : rawValue.length,
    );
    if (inner && !inner.includes('*') && !inner.includes('?')) {
      if (startsWithStar && endsWithStar) {
        return { op: op === 'ne' ? 'notContains' : 'contains', value: inner };
      }
      if (endsWithStar) return { op: 'beginsWith', value: inner };
      if (startsWithStar) return { op: 'endsWith', value: inner };
    }
  }
  const numeric = Number(rawValue);
  return {
    op,
    value: rawValue.trim() && Number.isFinite(numeric) ? numeric : rawValue,
  };
}

function parseFormula(body: string, localName: 'formula1' | 'formula2'): string | undefined {
  const formulaBody = firstElementBody(body, localName);
  if (formulaBody == null) return undefined;
  const nested = firstElementBody(formulaBody, 'f');
  const value = xmlUnescape(stripXmlTags(nested ?? formulaBody).trim());
  if (!value) return undefined;
  return value.startsWith('=') ? value.slice(1) : value;
}

export function parseSheetFeatures(sheetXml: string | null): XlsxParsedSheetFeatures {
  const output: XlsxParsedSheetFeatures = {
    validations: [],
    autoFilter: null,
    freeze: { rows: 0, cols: 0 },
    rowOutlineLevels: {},
  };
  if (!sheetXml) return output;

  const validationRe = /<(?:\w+:)?dataValidation\b([^>]*?)(?:\/\s*>|>([\s\S]*?)<\/(?:\w+:)?dataValidation\s*>)/gi;
  let match: RegExpExecArray | null;
  while ((match = validationRe.exec(sheetXml)) !== null) {
    const attrs = parseXmlAttributes(match[1] ?? '');
    const body = match[2] ?? '';
    const sqref = attrs.sqref ?? stripXmlTags(firstElementBody(body, 'sqref') ?? '').trim();
    if (!sqref) continue;
    const ranges = sqrefToRects(sqref);
    if (ranges.length === 0) continue;
    const validation: XlsxParsedValidation = {
      type: attrs.type || 'list',
      ranges,
    };
    if (attrs.operator) validation.operator = attrs.operator;
    if (attrs.allowBlank !== undefined) {
      validation.allowBlank = attrs.allowBlank === '1' || attrs.allowBlank === 'true';
    }
    if (attrs.prompt) validation.prompt = attrs.prompt;
    if (attrs.promptTitle) validation.promptTitle = attrs.promptTitle;
    if (attrs.error) validation.error = attrs.error;
    if (attrs.errorTitle) validation.errorTitle = attrs.errorTitle;
    const formula1 = parseFormula(body, 'formula1');
    const formula2 = parseFormula(body, 'formula2');
    if (formula2) validation.formula2 = formula2;
    if (formula1) {
      const staticValues = validation.type === 'list' ? parseStaticList(formula1) : null;
      if (staticValues) validation.staticValues = staticValues;
      else validation.formula1 = formula1;
    }
    output.validations.push(validation);
  }

  const autoFilterTag = /<(?:\w+:)?autoFilter\b([^>]*)>/i.exec(sheetXml);
  if (autoFilterTag) {
    const attrs = parseXmlAttributes(autoFilterTag[1]);
    if (attrs.ref) {
      const columns: XlsxAutoFilterColumn[] = [];
      const autoFilterBody = /<(?:\w+:)?autoFilter\b[^>]*>([\s\S]*?)<\/(?:\w+:)?autoFilter\s*>/i.exec(sheetXml)?.[1] ?? '';
      const filterColumnRe = /<(?:\w+:)?filterColumn\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?filterColumn\s*>/gi;
      let columnMatch: RegExpExecArray | null;
      while ((columnMatch = filterColumnRe.exec(autoFilterBody)) !== null) {
        const columnAttrs = parseXmlAttributes(columnMatch[1] ?? '');
        const colId = Number(columnAttrs.colId);
        if (!Number.isInteger(colId) || colId < 0) continue;
        const column: XlsxAutoFilterColumn = { colId };
        const body = columnMatch[2] ?? '';
        const filtersBody = firstElementBody(body, 'filters');
        if (filtersBody != null) {
          const values: string[] = [];
          const filterRe = /<(?:\w+:)?filter\b([^>]*?)\/?\s*>/gi;
          let filterMatch: RegExpExecArray | null;
          while ((filterMatch = filterRe.exec(filtersBody)) !== null) {
            const value = parseXmlAttributes(filterMatch[1] ?? '').val;
            if (value !== undefined) values.push(value);
          }
          if (values.length > 0) column.values = values;
        }
        const customMatch = /<(?:\w+:)?customFilters\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?customFilters\s*>/i.exec(body);
        if (customMatch) {
          const customAttrs = parseXmlAttributes(customMatch[1] ?? '');
          if (customAttrs.and === '1' || customAttrs.and === 'true') column.and = true;
          const custom: XlsxAutoFilterCustom[] = [];
          const conditionRe = /<(?:\w+:)?customFilter\b([^>]*?)\/?\s*>/gi;
          let conditionMatch: RegExpExecArray | null;
          while ((conditionMatch = conditionRe.exec(customMatch[2])) !== null) {
            const conditionAttrs = parseXmlAttributes(conditionMatch[1] ?? '');
            if (conditionAttrs.val === undefined) continue;
            custom.push(parseCustomFilter(conditionAttrs.operator, conditionAttrs.val));
          }
          if (custom.length > 0) column.custom = custom;
        }
        if (column.values || column.custom) columns.push(column);
      }
      output.autoFilter = columns.length > 0 ? { ref: attrs.ref, columns } : { ref: attrs.ref };
    }
  }

  const pane = /<(?:\w+:)?pane\b([^>]*?)\/?\s*>/i.exec(sheetXml);
  if (pane) {
    const attrs = parseXmlAttributes(pane[1] ?? '');
    if (attrs.state === 'frozen' || attrs.state === 'frozenSplit') {
      const rows = Number(attrs.ySplit ?? 0);
      const cols = Number(attrs.xSplit ?? 0);
      output.freeze = {
        rows: Number.isFinite(rows) ? Math.max(0, Math.trunc(rows)) : 0,
        cols: Number.isFinite(cols) ? Math.max(0, Math.trunc(cols)) : 0,
      };
    }
  }

  const sheetData = firstElementBody(sheetXml, 'sheetData') ?? sheetXml;
  const rowRe = /<(?:\w+:)?row\b([^>]*)>/gi;
  while ((match = rowRe.exec(sheetData)) !== null) {
    const attrs = parseXmlAttributes(match[1] ?? '');
    const row = Number(attrs.r);
    const level = Number(attrs.outlineLevel);
    if (!Number.isInteger(row) || row <= 0 || !Number.isFinite(level)) continue;
    const clamped = Math.min(7, Math.max(0, Math.trunc(level)));
    if (clamped > 0) output.rowOutlineLevels[String(row)] = clamped;
  }
  return output;
}

function argbToHex(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/^#/, '').trim().toUpperCase();
  if (/^[0-9A-F]{8}$/.test(normalized)) return `#${normalized.slice(2)}`;
  if (/^[0-9A-F]{6}$/.test(normalized)) return `#${normalized}`;
  return undefined;
}

export function parseDxfs(stylesXml: string | null): CfDxf[] {
  if (!stylesXml) return [];
  const block = firstElementBody(stylesXml, 'dxfs');
  if (block == null) return [];
  const dxfs: CfDxf[] = [];
  const dxfRe = /<(?:\w+:)?dxf\b[^>]*>([\s\S]*?)<\/(?:\w+:)?dxf\s*>/gi;
  let match: RegExpExecArray | null;
  while ((match = dxfRe.exec(block)) !== null) {
    const body = match[1] ?? '';
    const dxf: CfDxf = {};
    const font = firstElementBody(body, 'font');
    if (font != null) {
      const colorTag = /<(?:\w+:)?color\b([^>]*?)\/?\s*>/i.exec(font);
      const color = argbToHex(colorTag ? parseXmlAttributes(colorTag[1]).rgb : undefined);
      if (color) dxf.color = color;
      if (/<(?:\w+:)?b\b[^>]*?\/?\s*>/i.test(font)) dxf.fontWeight = 'bold';
      if (/<(?:\w+:)?i\b[^>]*?\/?\s*>/i.test(font)) dxf.fontStyle = 'italic';
    }
    const patternFill = firstElementBody(body, 'patternFill');
    if (patternFill != null) {
      const backgroundTag = /<(?:\w+:)?(?:bgColor|fgColor)\b([^>]*?)\/?\s*>/i.exec(patternFill);
      const color = argbToHex(backgroundTag ? parseXmlAttributes(backgroundTag[1]).rgb : undefined);
      if (color) dxf.backgroundColor = color;
    }
    dxfs.push(dxf);
  }
  return dxfs;
}

export function parseConditionalFormats(
  sheetXml: string | null,
  stylesXml: string | null,
): CfBlock[] {
  if (!sheetXml || !/<(?:\w+:)?conditionalFormatting\b/i.test(sheetXml)) return [];
  const dxfList = parseDxfs(stylesXml);
  const blocks: CfBlock[] = [];
  const blockRe = /<(?:\w+:)?conditionalFormatting\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?conditionalFormatting\s*>/gi;
  let blockMatch: RegExpExecArray | null;
  while ((blockMatch = blockRe.exec(sheetXml)) !== null) {
    const attrs = parseXmlAttributes(blockMatch[1] ?? '');
    if (!attrs.sqref) continue;
    const rects = sqrefToRects(attrs.sqref);
    if (rects.length === 0) continue;
    const rules: CfRuleParsed[] = [];
    const ruleRe = /<(?:\w+:)?cfRule\b([^>]*?)(?:\/\s*>|>([\s\S]*?)<\/(?:\w+:)?cfRule\s*>)/gi;
    let ruleMatch: RegExpExecArray | null;
    while ((ruleMatch = ruleRe.exec(blockMatch[2] ?? '')) !== null) {
      const ruleAttrs = parseXmlAttributes(ruleMatch[1] ?? '');
      if (!ruleAttrs.type) continue;
      const body = ruleMatch[2] ?? '';
      const rule: CfRuleParsed = {
        type: ruleAttrs.type,
        priority: Number.parseInt(ruleAttrs.priority ?? '0', 10) || 0,
        formulas: [],
      };
      if (ruleAttrs.operator) rule.operator = ruleAttrs.operator;
      if (ruleAttrs.text) rule.text = ruleAttrs.text;
      const formulaRe = /<(?:\w+:)?formula\b[^>]*>([\s\S]*?)<\/(?:\w+:)?formula\s*>/gi;
      let formulaMatch: RegExpExecArray | null;
      while ((formulaMatch = formulaRe.exec(body)) !== null) {
        const formula = xmlUnescape(stripXmlTags(formulaMatch[1]).trim());
        if (formula) rule.formulas.push(formula.startsWith('=') ? formula.slice(1) : formula);
      }

      if (rule.type === 'colorScale') {
        const colorScale = firstElementBody(body, 'colorScale');
        if (colorScale != null) {
          const cfvo: Array<{ type: string; val?: number }> = [];
          const cfvoRe = /<(?:\w+:)?cfvo\b([^>]*?)\/?\s*>/gi;
          let cfvoMatch: RegExpExecArray | null;
          while ((cfvoMatch = cfvoRe.exec(colorScale)) !== null) {
            const cfvoAttrs = parseXmlAttributes(cfvoMatch[1] ?? '');
            const value = cfvoAttrs.val === undefined ? undefined : Number(cfvoAttrs.val);
            cfvo.push({
              type: cfvoAttrs.type || 'min',
              val: value !== undefined && Number.isFinite(value) ? value : undefined,
            });
          }
          const colors: string[] = [];
          const colorRe = /<(?:\w+:)?color\b([^>]*?)\/?\s*>/gi;
          let colorMatch: RegExpExecArray | null;
          while ((colorMatch = colorRe.exec(colorScale)) !== null) {
            const color = argbToHex(parseXmlAttributes(colorMatch[1] ?? '').rgb);
            if (color) colors.push(color);
          }
          if (cfvo.length >= 2 && colors.length >= 2) rule.colorScale = { cfvo, colors };
        }
      } else if (ruleAttrs.dxfId !== undefined) {
        const dxfId = Number(ruleAttrs.dxfId);
        if (Number.isInteger(dxfId) && dxfList[dxfId]) rule.dxf = dxfList[dxfId];
      }
      // Preserve unsupported rules too; renderer evaluation may ignore them, but round-trip code can retain them.
      rules.push(rule);
    }
    if (rules.length > 0) blocks.push({ rects, rules });
  }
  return blocks;
}
